import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';

const CASE_LABELS = {
  critical: 'Critical',
  non_critical: 'Non-critical',
};

export function formatDurationHours(minutes) {
  if (!minutes) return '';
  const hours = minutes / 60;
  if (hours >= 24 && hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? '24 hours' : `${hours} hours`;
  }
  if (minutes % 60 === 0) return `${hours} hour${hours === 1 ? '' : 's'}`;
  return `${minutes} minutes`;
}

export default function BookingRequirementsForm({
  bookingRequirements,
  caseType,
  onCaseTypeChange,
  durationOptionId,
  onDurationOptionChange,
  customDurationMinutes,
  onCustomDurationMinutesChange,
  foodAcknowledged,
  onFoodAcknowledgedChange,
  stayAcknowledged,
  onStayAcknowledgedChange,
  selectedDurationMinutes,
}) {
  if (!bookingRequirements) return null;

  const caseTypes = bookingRequirements.case_types_enabled || [];
  const durationOptions = bookingRequirements.duration_options || [];
  const selectedOption = durationOptions.find((o) => o.id === durationOptionId);
  const foodRequired = Boolean(selectedOption?.food_included);
  const stayRequired = Boolean(selectedOption?.stay_included);

  const longOptionSelected = durationOptions.find((o) => o.id === durationOptionId)?.is_long_duration;

  return (
    <div className="space-y-4" data-testid="booking-requirements-form">
      {caseTypes.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-slate-600">Case type</Label>
          <Select value={caseType || ''} onValueChange={onCaseTypeChange}>
            <SelectTrigger data-testid="booking-case-type">
              <SelectValue placeholder="Select case type" />
            </SelectTrigger>
            <SelectContent>
              {caseTypes.map((ct) => (
                <SelectItem key={ct} value={ct} data-testid={`case-type-${ct}`}>
                  {CASE_LABELS[ct] || ct}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs font-semibold text-slate-600">Duration</Label>
        <Select value={durationOptionId || ''} onValueChange={onDurationOptionChange}>
          <SelectTrigger data-testid="booking-duration">
            <SelectValue placeholder="Select duration" />
          </SelectTrigger>
          <SelectContent>
            {durationOptions.map((opt) => (
              <SelectItem
                key={opt.id}
                value={opt.id}
                data-testid={`duration-option-${opt.id}`}
              >
                {opt.label}
                {opt.food_included ? ' + food' : ''}
                {opt.stay_included ? (opt.food_included ? ' + stay' : ' + food + stay') : ''}
                {opt.price != null ? ` — ₹${opt.price}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {longOptionSelected && (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Duration (hours, above 24)</Label>
            <Input
              data-testid="booking-custom-duration-hours"
              type="number"
              min={25}
              max={720}
              value={customDurationMinutes ? Math.round(customDurationMinutes / 60) : ''}
              onChange={(e) => {
                const hours = Number(e.target.value);
                onCustomDurationMinutesChange(hours > 0 ? hours * 60 : '');
              }}
              placeholder="e.g. 72"
            />
          </div>
        )}
      </div>

      <div className="text-xs text-slate-500" data-testid="booking-quantity-locked">
        Providers: 1 (one provider per booking)
      </div>

      {foodRequired && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3">
          <Checkbox
            id="food-ack"
            data-testid="food-acknowledgement"
            checked={foodAcknowledged}
            onCheckedChange={(v) => onFoodAcknowledgedChange(Boolean(v))}
          />
          <Label htmlFor="food-ack" className="text-xs text-amber-900 leading-snug">
            Food arrangements are included with this option; customer must provide food for the service provider.
          </Label>
        </div>
      )}

      {stayRequired && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3">
          <Checkbox
            id="stay-ack"
            data-testid="stay-acknowledgement"
            checked={stayAcknowledged}
            onCheckedChange={(v) => onStayAcknowledgedChange(Boolean(v))}
          />
          <Label htmlFor="stay-ack" className="text-xs text-amber-900 leading-snug">
            Food and stay are included with this option; customer must arrange suitable food and accommodation.
          </Label>
        </div>
      )}
    </div>
  );
}
